import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import type {
  ACL,
  Action,
  Environment,
  EvalOptions,
  EvaluationResult,
  Resource,
  Scope,
  User,
} from "@shield-acl/core";
import { mergeEnv } from "./hooks/internal";

/**
 * Opções de uma checagem no React.
 *
 * - `scope`: override do scope do Provider (checar OUTRO app).
 * - `record`: INSTÂNCIA do recurso (owner, status...) → vira `ctx.resource`.
 * - `environment`: sobrepõe (merge raso) o environment do Provider.
 */
export interface CheckOptions<TRecord = unknown> {
  scope?: Scope;
  record?: TRecord;
  environment?: Environment;
}

export interface ACLContextValue {
  engine: ACL;
  scope: Scope;
  environment: Environment | undefined;
  user: User | null;
  setUser: (user: User | null) => void;
  /** Revisão reativa do engine (bumpa quando roles mudam). */
  revision: number;
  can: (action: Action, resource?: Resource, opts?: CheckOptions) => boolean;
  evaluate: (
    action: Action,
    resource?: Resource,
    opts?: CheckOptions,
  ) => EvaluationResult;
  canAsync: (
    action: Action,
    resource?: Resource,
    opts?: CheckOptions,
  ) => Promise<EvaluationResult>;
}

const ACLContext = createContext<ACLContextValue | undefined>(undefined);

export interface ACLProviderProps {
  engine: ACL;
  /** Usuário: use como inicial (não-controlado) ou atualize a prop (controlado). */
  user?: User | null;
  /** Scope default da subárvore. Default "*". */
  scope?: Scope;
  /** Environment default (reativo: MFA/hora/IP). */
  environment?: Environment;
  children: ReactNode;
}

/**
 * Provider do ACL — scope + environment reativos e engine observável.
 */
export function ACLProvider({
  engine,
  user: userProp,
  scope = "*",
  environment,
  children,
}: ACLProviderProps) {
  // Estado interno semeado pela prop; ressincroniza se a prop mudar (controlado).
  const [user, setUser] = useState<User | null>(userProp ?? null);
  useEffect(() => {
    if (userProp !== undefined) setUser(userProp);
  }, [userProp]);

  // Reatividade a mudanças de roles: assina a revisão do engine.
  const revision = useSyncExternalStore(
    engine.subscribe,
    () => engine.revision,
    () => engine.revision,
  );

  const can = useCallback(
    (action: Action, resource?: Resource, opts?: CheckOptions): boolean => {
      if (!user) return false;
      return engine.can(
        user,
        opts?.scope ?? scope,
        action,
        resource,
        toEval(opts, environment),
      );
    },
    // revision garante reavaliação quando o catálogo de roles muda
    [engine, user, scope, environment, revision],
  );

  const evaluate = useCallback(
    (
      action: Action,
      resource?: Resource,
      opts?: CheckOptions,
    ): EvaluationResult => {
      const activeScope = opts?.scope ?? scope;
      if (!user) {
        return {
          allowed: false,
          reason: "No user authenticated",
          scope: activeScope,
        };
      }
      return engine.evaluate(
        user,
        activeScope,
        action,
        resource,
        toEval(opts, environment),
      );
    },
    [engine, user, scope, environment, revision],
  );

  const canAsync = useCallback(
    (
      action: Action,
      resource?: Resource,
      opts?: CheckOptions,
    ): Promise<EvaluationResult> => {
      const activeScope = opts?.scope ?? scope;
      if (!user) {
        return Promise.resolve({
          allowed: false,
          reason: "No user authenticated",
          scope: activeScope,
        });
      }
      return engine.evaluateAsync(
        user,
        activeScope,
        action,
        resource,
        toEval(opts, environment),
      );
    },
    [engine, user, scope, environment, revision],
  );

  const value = useMemo<ACLContextValue>(
    () => ({
      engine,
      scope,
      environment,
      user,
      setUser,
      revision,
      can,
      evaluate,
      canAsync,
    }),
    [engine, scope, environment, user, revision, can, evaluate, canAsync],
  );

  return <ACLContext.Provider value={value}>{children}</ACLContext.Provider>;
}

function toEval(
  opts: CheckOptions | undefined,
  providerEnv: Environment | undefined,
): EvalOptions {
  return {
    resource: opts?.record,
    environment: mergeEnv(providerEnv, opts?.environment),
  };
}

export function useACLContext(): ACLContextValue {
  const context = useContext(ACLContext);
  if (!context) {
    throw new Error("useACLContext must be used within an ACLProvider");
  }
  return context;
}
